import { useSession } from "@clerk/clerk-react";
import * as Lucide from "lucide-react";
import { ChevronLeft, Loader2, SearchIcon, XIcon } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import {
  useCanvasPos,
  useCanvasStore,
} from "@/components/Canvas/stores/canvasStore";
import { useCreateStoryboard } from "@/hooks/storyboards/useCreateStoryboard";
import { useInitializeStoryboard } from "@/hooks/storyboards/useInitializeStoryboards";
import {
  type AssetComponent,
  isLibraryComponent,
  useAssetComponents,
} from "@/hooks/useAssetComponents";
import { cn } from "@/lib/utils";
import { setSelectedElementKey } from "@/store/iframeSlice";
import {
  type Tab,
  useAssetsPanelStore,
} from "@/store/zustand/assetsPanelStore";
import {
  AddLibraryComponentChange,
  RemoveLibraryComponentChange,
} from "@/store/zustand/changeLedgerTypes";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import {
  LeftTab,
  default as useLayoutStore,
} from "@/store/zustand/layoutStore";
import { usePagesStore } from "@/store/zustand/pagesStore";
import { STORYBOARD_TYPES } from "@/utils/constants";
import type { JsxElement } from "@/utils/jsx/jsxElementTypes";
import { TempoElement } from "@/utils/tempoElement";
import type { SubCanvas } from "@/utils/types";
import {
  getRelativePathFromCanvas,
  useActiveCanvas,
  useCanvasId,
} from "@/utils/utils";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { AssetItem } from "./AssetItem";
import { DetailsPanel } from "./DetailsPanel";
import LucideReactGallery from "./LucideReactGallery";
import { InstancesPanel } from "./instances/InstancesPanel";

interface AssetGridProps {
  activeTab: Tab;
  components: AssetComponent[];
  selectedAsset: JsxElement | null;
  setSelectedAsset: (asset: JsxElement | null) => void;
  detailedAsset: JsxElement | null;
  setDetailedAsset: (asset: JsxElement | null) => void;
  handleAddToCanvas: (
    assetToAdd: JsxElement,
    variantToAdd?: JsxElement,
    propsToSet?: { [key: string]: any },
  ) => void;
  getVariants?: (component: JsxElement) => JsxElement[];
  instancesPanel?: React.ReactNode;
  onAddToLibrary?: (component: AssetComponent) => void;
  onRemoveFromLibrary?: (component: AssetComponent) => void;
}

const AssetGrid = React.memo(
  ({
    activeTab,
    components,
    selectedAsset,
    setSelectedAsset,
    detailedAsset,
    setDetailedAsset,
    handleAddToCanvas,
    getVariants = () => [],
    instancesPanel,
    onAddToLibrary,
    onRemoveFromLibrary,
  }: AssetGridProps) => {
    // Determine if we're showing variants (selectedAsset is set)
    const isShowingVariants = !!selectedAsset;

    const handleItemClick = useCallback(
      (component: AssetComponent) => {
        if (isShowingVariants) {
          // When viewing variants, clicking opens the details popover
          setDetailedAsset(
            detailedAsset?.codebaseId === component.codebaseId
              ? null
              : component,
          );
        } else {
          // When viewing main component list, clicking selects the component
          setSelectedAsset(component);
        }
      },
      [
        isShowingVariants,
        setSelectedAsset,
        setDetailedAsset,
        detailedAsset?.codebaseId,
      ],
    );

    const handleAddToCanvasWithVariant = useCallback(
      (
        component: JsxElement,
        variant?: JsxElement,
        propsToSet?: { [key: string]: any },
      ) => {
        handleAddToCanvas(component, variant, propsToSet);
      },
      [handleAddToCanvas],
    );

    const selectedAssetVariants = useMemo(() => {
      if (!selectedAsset) return undefined;
      const variants = getVariants(selectedAsset);
      // Return variants if any exist, otherwise show the component itself
      return variants.length > 0 ? variants : [selectedAsset];
    }, [selectedAsset, getVariants]);

    return (
      <div
        data-testid="assets-panel-grid"
        className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-2 p-4 overflow-y-scroll h-full auto-rows-max scrollbar-hide"
      >
        {/* Show breadcrumb navigation when viewing variants of a selected component */}
        {selectedAsset && (
          <div className="col-span-full text-xs flex items-center">
            <button
              type="button"
              onClick={() => {
                setSelectedAsset(null);
                setDetailedAsset(null);
              }}
              className="flex items-center gap-1 cursor-pointer text-text-primary hover:text-text-secondary"
            >
              <ChevronLeft className="w-4 h-4" />
              <div
                className="text-primary truncate shrink max-w-44"
                title={selectedAsset?.componentName || ""}
              >
                {selectedAsset?.componentName}
              </div>
            </button>
          </div>
        )}
        {(selectedAssetVariants ?? components).map((component) => {
          const variants = getVariants(component);
          const isSelectedForDetails =
            isShowingVariants &&
            detailedAsset?.codebaseId === component.codebaseId;
          // Check if this component is a library component (only applies to AssetComponent type)
          const isInLibrary = isLibraryComponent(component);

          // Show popover only when viewing variants
          if (isShowingVariants) {
            return (
              <Popover
                key={component.key}
                open={isSelectedForDetails}
                onOpenChange={(open) => !open && setDetailedAsset(null)}
              >
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      "relative group cursor-pointer rounded-md transition-colors",
                      detailedAsset?.key === component.key
                        ? "bg-secondary-200 border-secondary-300"
                        : "hover:bg-secondary-100",
                    )}
                    onClick={() => handleItemClick(component)}
                  >
                    <AssetItem
                      element={component}
                      variants={variants}
                      selected={isSelectedForDetails}
                      isLibraryComponent={isInLibrary}
                      onAddToLibrary={
                        !isInLibrary && onAddToLibrary
                          ? () => onAddToLibrary(component)
                          : undefined
                      }
                      onRemoveFromLibrary={
                        isInLibrary && onRemoveFromLibrary
                          ? () => onRemoveFromLibrary(component)
                          : undefined
                      }
                    />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="right"
                  align="start"
                  className="p-0 border-none w-auto bg-transparent shadow-none"
                >
                  {isSelectedForDetails && (
                    <DetailsPanel
                      component={component}
                      variants={variants}
                      onClose={() => setDetailedAsset(null)}
                      onAddToCanvas={(variant, propsToSet) =>
                        handleAddToCanvasWithVariant(
                          component,
                          variant,
                          propsToSet,
                        )
                      }
                    />
                  )}
                </PopoverContent>
              </Popover>
            );
          }

          // Main component list doesn't show a popover even if theres only one
          // known variant since we always want to be able to display the
          // Instances list
          return (
            <div
              key={component.key}
              className={cn(
                "relative group cursor-pointer rounded-md transition-colors",
                "hover:bg-background-secondary",
              )}
              onClick={() => handleItemClick(component as AssetComponent)}
            >
              <AssetItem
                element={component}
                variants={variants}
                selected={false}
                isLibraryComponent={isInLibrary}
                onAddToLibrary={
                  !isInLibrary && onAddToLibrary
                    ? () => onAddToLibrary(component as AssetComponent)
                    : undefined
                }
                onRemoveFromLibrary={
                  isInLibrary && onRemoveFromLibrary
                    ? () => onRemoveFromLibrary(component as AssetComponent)
                    : undefined
                }
              />
            </div>
          );
        })}
        {/* Instances Panel - render below variants when component is selected */}
        {instancesPanel && (
          <div className="col-span-full">{instancesPanel}</div>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function to prevent unnecessary re-renders
    return (
      prevProps.components.length === nextProps.components.length &&
      prevProps.components.every(
        (comp, i) => comp.key === nextProps.components[i].key,
      ) &&
      prevProps.selectedAsset?.codebaseId ===
        nextProps.selectedAsset?.codebaseId &&
      prevProps.detailedAsset?.codebaseId ===
        nextProps.detailedAsset?.codebaseId &&
      prevProps.instancesPanel === nextProps.instancesPanel
    );
  },
);

export type AssetsPanelProps = {};

interface TabButtonProps {
  tab: string;
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

const TabButton = React.memo(
  ({ tab, activeTab, setActiveTab }: TabButtonProps) => (
    <button
      type="button"
      className={cn(
        "p-0 text-xs font-semibold transition-colors hover:text-text-primary",
        activeTab === tab ? "text-secondary-900" : "text-secondary-700",
      )}
      onClick={() => setActiveTab(tab)}
    >
      {tab}
    </button>
  ),
);

interface SearchInputProps {
  searchVal: string;
  setSearchVal: (val: string) => void;
  activeTab: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearSearch: () => void;
}

const SearchInput = React.memo(
  ({
    searchVal,
    activeTab,
    onSearchChange,
    onClearSearch,
  }: SearchInputProps) => (
    <div className="group w-full pr-0">
      <div className="relative">
        <Input
          data-testid="assets-search-input"
          placeholder={`Search ${activeTab}`}
          className="w-full pl-9 h-7 bg-secondary-200 text-xs text-secondary-n-50 placeholder:text-secondary-n-600 border-0 focus-within:border-primary-0 focus-within:border transition"
          value={searchVal}
          onChange={onSearchChange}
        />
        <SearchIcon
          className={
            "absolute top-1/2 left-3 -translate-y-1/2 w-4 h-4 text-text-muted group-focus-within:text-primary-0 transition"
          }
        />
        {searchVal && (
          <button
            type="button"
            className="absolute top-1/2 right-3 -translate-y-1/2 text-secondary-500 hover:text-white"
            onClick={onClearSearch}
          >
            <XIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  ),
);

export const AssetsPanel = () => {
  const {
    searchVal,
    setSearchVal,
    activeTab,
    setActiveTab,
    variantSelected,
    setVariantSelected,
    selectedAsset,
    setSelectedAsset,
    detailedAsset,
    setDetailedAsset,
  } = useAssetsPanelStore();

  const canvasComponentKey = useCanvasStore(
    (store) => store.canvasComponentKey,
  );

  const canvas = useActiveCanvas();
  const dispatch = useDispatch<any>();
  const initializeStoryboard = useInitializeStoryboard();
  const createStoryboard = useCreateStoryboard();
  const canvasId = useCanvasId();
  const selectedPageId = usePagesStore(
    (state) => state.selectedPageId[canvasId],
  );
  const { transformX, transformY } = useCanvasPos();
  const { leftExpanded, rightExpanded, leftTab } = useLayoutStore((state) => ({
    leftExpanded: state.leftExpanded,
    rightExpanded: state.rightExpanded,
    leftTab: state.leftTab,
  }));
  const isAssetsPanelVisible = leftTab === LeftTab.Assets;

  const { session } = useSession();
  const { executeCommand } = useDesignCommandManager();
  const [allLucideIcons, setAllLucideIcons] = React.useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const allIcons = Array.from(Object.keys(Lucide)).filter(
      (iconName: string) =>
        iconName.endsWith("Icon") &&
        iconName !== "createLucideIcon" &&
        iconName !== "Icon",
    );
    setAllLucideIcons(allIcons);
  }, []);

  const filteredIcons = useMemo(() => {
    if (!searchVal) {
      return allLucideIcons;
    }
    return allLucideIcons.filter((iconName: string) =>
      iconName.toLowerCase().includes(searchVal.toLowerCase()),
    );
  }, [searchVal, allLucideIcons]);

  const {
    componentKeyToComponent,
    componentKeyToStories,
    standaloneStoryFiles,
    standaloneStoryFileToStories,
  } = useAssetComponents();

  useEffect(() => {
    if (Object.keys(componentKeyToComponent).length > 0) {
      setIsLoading(false);
    }
  }, [componentKeyToComponent]);

  // Helper to get variants for any component (checks both story maps)
  const getVariantsForComponent = useCallback(
    (component: JsxElement): JsxElement[] => {
      // First check componentKeyToStories (for components with meta.component)
      if (componentKeyToStories[component.key]?.length) {
        return componentKeyToStories[component.key];
      }
      // Then check standaloneStoryFileToStories (for story files without meta.component)
      if (standaloneStoryFileToStories[component.key]?.length) {
        return standaloneStoryFileToStories[component.key];
      }
      return [];
    },
    [componentKeyToStories, standaloneStoryFileToStories],
  );

  // Build unified and sorted component list for the Components tab
  // Sorting order: Library Components → Components with Stories → Standalone Story Files → Components without Stories
  const unifiedComponentsFiltered = useMemo(() => {
    const libraryComponents: AssetComponent[] = [];
    const componentsWithStories: AssetComponent[] = [];
    const componentsWithoutStories: AssetComponent[] = [];

    // Categorize all components
    Object.keys(componentKeyToComponent).forEach((componentKey) => {
      const component = componentKeyToComponent[componentKey];
      const hasStories = componentKeyToStories[componentKey]?.length > 0;

      if (isLibraryComponent(component)) {
        libraryComponents.push(component);
      } else if (hasStories) {
        componentsWithStories.push(component);
      } else {
        componentsWithoutStories.push(component);
      }
    });

    // Sort each category alphabetically by component name
    const sortByName = (a: AssetComponent, b: AssetComponent) =>
      (a.componentName || "").localeCompare(b.componentName || "");

    libraryComponents.sort(sortByName);
    componentsWithStories.sort(sortByName);
    componentsWithoutStories.sort(sortByName);
    // Sort standalone story files by their display name (which is set as componentName)
    const sortedStandaloneStoryFiles = [...standaloneStoryFiles].sort(
      sortByName,
    );

    // Combine in the specified order:
    // 1. Library Components
    // 2. Components with Stories
    // 3. Standalone Story Files (story files without meta.component)
    // 4. Components without Stories
    const allComponents = [
      ...libraryComponents,
      ...componentsWithStories,
      ...sortedStandaloneStoryFiles,
      ...componentsWithoutStories,
    ];

    // Apply search filter if present
    if (!searchVal) {
      return allComponents;
    }

    const searchLower = searchVal.toLowerCase();
    return allComponents.filter((component) => {
      // Match component name
      if (component.componentName?.toLowerCase().includes(searchLower)) {
        return true;
      }

      // For components with stories, also search story names (both maps)
      const stories =
        componentKeyToStories[component.key] ||
        standaloneStoryFileToStories[component.key];
      if (stories?.length) {
        return stories.some((story) =>
          story.componentName?.toLowerCase().includes(searchLower),
        );
      }

      return false;
    });
  }, [
    searchVal,
    componentKeyToComponent,
    componentKeyToStories,
    standaloneStoryFiles,
    standaloneStoryFileToStories,
  ]);

  // Updated tabs: Storybook tab has been merged into Components
  const tabs = useMemo(() => ["Components", "Icons"] as const, []);

  // Handle adding a component to the library configuration
  const handleAddToLibrary = useCallback(
    (component: AssetComponent) => {
      if (!canvasId) {
        toast.error("Cannot add to library: missing canvas");
        return;
      }

      // Convert absolute path to canvas-relative path without extension
      const relativePath = getRelativePathFromCanvas(
        component.filename,
        canvasId,
      );
      const relativePathWithoutExt = relativePath.replace(/\.[^/.]+$/, "");

      // Get existing default library or generate new ID for a new library
      const libraryId =
        useCodebaseStore.getState().getDefaultLibraryId() ?? uuidv4();

      const change = new AddLibraryComponentChange({
        canvasId,
        componentPath: relativePathWithoutExt,
        componentName: component.componentName,
        libraryId,
      });

      executeCommand(change);
      toast.success(
        `Added "${component.componentName || "Component"}" to library`,
      );
    },
    [canvasId, executeCommand],
  );

  // Handle removing a component from the library configuration
  const handleRemoveFromLibrary = useCallback(
    (component: AssetComponent) => {
      if (!canvasId) {
        toast.error("Cannot remove from library: missing canvas");
        return;
      }

      if (!component.libraryId) {
        toast.error("Cannot remove from library: component has no library ID");
        return;
      }

      // Convert absolute path to canvas-relative path without extension
      const relativePath = getRelativePathFromCanvas(
        component.filename,
        canvasId,
      );
      const relativePathWithoutExt = relativePath.replace(/\.[^/.]+$/, "");

      const change = new RemoveLibraryComponentChange({
        canvasId,
        componentPath: relativePathWithoutExt,
        componentName: component.componentName,
        libraryId: component.libraryId,
      });

      executeCommand(change);
      toast.success(
        `Removed "${component.componentName || "Component"}" from library`,
      );
    },
    [canvasId, executeCommand],
  );

  const handleAddToCanvas = useCallback(
    async (
      assetToAdd: JsxElement,
      variantToAdd?: JsxElement,
      propsToSet?: { [key: string]: any },
    ) => {
      const itemToAdd = variantToAdd || assetToAdd;

      if (!canvas) {
        console.error("Active canvas not found.");
        return;
      }

      if (!itemToAdd.codebaseId) {
        console.error(
          "Component to add does not have a codebaseId:",
          itemToAdd,
        );
        return;
      }

      // try to approximate the canvas center
      const currentTransformX = transformX || 0;
      const currentTransformY = transformY || 0;

      const leftPanelWidth = leftExpanded ? 300 : 0;
      const rightPanelWidth = rightExpanded ? 300 : 0;
      const headerHeight = 50;

      const estimatedViewportWidth =
        window.innerWidth - leftPanelWidth - rightPanelWidth;
      const estimatedViewportHeight = window.innerHeight - headerHeight;

      // default fixed size to be smaller than usual storyboards
      const sbWidth = 400;
      const sbHeight = 300;

      const centerX_viewport = estimatedViewportWidth / 2;
      const centerY_viewport = estimatedViewportHeight / 2;

      const centerX_canvas = centerX_viewport + currentTransformX;
      const centerY_canvas = centerY_viewport + currentTransformY;

      const finalX = centerX_canvas - sbWidth / 2;
      const finalY = centerY_canvas - sbHeight / 2;

      // useful debug
      // console.log("Storyboard Placement Calculation (Attempt 2: +transform):", {
      //     zoom,
      //     currentTransformX,
      //     currentTransformY,
      //     estimatedViewportWidth, // Using estimated width now
      //     estimatedViewportHeight, // Using estimated height now
      //     leftPanelWidth, // Log panel widths used
      //     rightPanelWidth, // Log panel widths used
      //     centerX_canvas,
      //     centerY_canvas,
      //     sbWidth,        // Storyboard width
      //     sbHeight,       // Use sbHeight here
      //     finalX,
      //     finalY
      // });
      //

      const newStoryboard = initializeStoryboard(
        finalX,
        finalY,
        sbWidth,
        sbHeight,
      );

      newStoryboard.resizeOnceRendered = true;

      const componentToAddArg = {
        componentId: itemToAdd.codebaseId,
        propsToSet,
      };

      try {
        await createStoryboard({
          storyboard: newStoryboard,
          pageId: selectedPageId || "",
          componentToAdd: componentToAddArg,
          actionText: "Added Component from Panel",
        });
      } catch (error) {
        console.error("Failed to create storyboard:", error);
        toast.error("Failed to create storyboard:", error);
      }
    },
    [
      canvas,
      transformX,
      transformY,
      leftExpanded,
      rightExpanded,
      initializeStoryboard,
      createStoryboard,
    ],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchVal(e.target.value);
      setSelectedAsset(null);
    },
    [setSearchVal, setSelectedAsset],
  );

  const handleClearSearch = useCallback(() => {
    setSearchVal("");
  }, [setSearchVal]);

  const handleTabChange = useCallback(
    (tab: Tab) => {
      setActiveTab(tab);
    },
    [setActiveTab],
  );

  useEffect(() => {
    if (
      !variantSelected ||
      !canvasComponentKey ||
      !canvas ||
      !canvas.storyboards ||
      (canvas as SubCanvas).component_id !== canvasComponentKey
    ) {
      return;
    }

    const selectVariant = async () => {
      const storyboardToSelect = canvas.storyboards.find(
        (storyboard) =>
          storyboard.type === STORYBOARD_TYPES.STORY &&
          storyboard.name === variantSelected.story.componentName,
      );

      if (!storyboardToSelect) {
        return;
      }

      dispatch(
        setSelectedElementKey(
          TempoElement.forStoryboard(storyboardToSelect.id),
        ),
      );
      setVariantSelected(null);
    };

    selectVariant();
  }, [
    variantSelected,
    canvasComponentKey,
    canvas,
    dispatch,
    setVariantSelected,
  ]);

  return (
    <>
      <div className="w-full h-full flex flex-col bg-secondary-0">
        <div className="flex font-medium text-xs p-2.5 gap-2.5">
          {tabs.map((tab) => (
            <TabButton
              key={tab}
              tab={tab}
              activeTab={activeTab}
              setActiveTab={handleTabChange}
            />
          ))}
        </div>

        <div className="flex items-center justify-between border-y border-border-primary p-4 gap-2">
          <SearchInput
            searchVal={searchVal}
            setSearchVal={setSearchVal}
            activeTab={activeTab}
            onSearchChange={handleSearchChange}
            onClearSearch={handleClearSearch}
          />
        </div>
        <div className="overflow-y-auto scrollbar-hide flex-1 relative">
          {isLoading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-primary-0" />
            </div>
          ) : (
            <>
              {activeTab === "Components" &&
                (unifiedComponentsFiltered.length === 0 ? (
                  <div className="text-primary text-xs p-4">
                    {searchVal
                      ? `0 results for "${searchVal}"`
                      : "No components found."}
                  </div>
                ) : (
                  <AssetGrid
                    activeTab={activeTab}
                    components={unifiedComponentsFiltered}
                    selectedAsset={selectedAsset}
                    setSelectedAsset={setSelectedAsset}
                    detailedAsset={detailedAsset}
                    setDetailedAsset={setDetailedAsset}
                    handleAddToCanvas={handleAddToCanvas}
                    getVariants={getVariantsForComponent}
                    instancesPanel={
                      selectedAsset && isAssetsPanelVisible ? (
                        <InstancesPanel selectedAsset={selectedAsset} />
                      ) : undefined
                    }
                    // TODO: Re-enable once library components backend is merged
                    // onAddToLibrary={handleAddToLibrary}
                    // onRemoveFromLibrary={handleRemoveFromLibrary}
                  />
                ))}

              {activeTab === "Icons" &&
                (filteredIcons.length === 0 ? (
                  <div className="text-primary text-xs p-4">
                    {searchVal
                      ? `0 results for "${searchVal}"`
                      : "No icons found."}
                  </div>
                ) : (
                  <div className="col-span-2">
                    <LucideReactGallery icons={filteredIcons} />
                  </div>
                ))}
            </>
          )}
        </div>
      </div>
    </>
  );
};
